// Utility functions for JWT token handling and subscription checking
import { PREMIUM_SUBSCRIPTION_TYPES } from 'constants/PremiumConstant';
import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
/**
 * Decodes a JWT token without validation
 * @param {string} token - The JWT token to decode
 * @returns {Object|null} - The decoded token payload or null if invalid
 */
export const decodeToken = (token) => {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (middle part)
    // Add padding if needed for base64 decoding
    let payload = parts[1];
    while (payload.length % 4) {
      payload += '=';
    }

    const decodedPayload = JSON.parse(atob(payload));

    return decodedPayload;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Checks if a user has a premium subscription based on their JWT token
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if the user has a premium subscription
 */
export const isPremiumUser = (token) => {
  if (!token) {
    return false;
  }

  const decoded = decodeToken(token);
  if (!decoded) {
    return false;
  }

  // Check if the subscription_type is one of the premium types
  // const premiumTypes = ['Professional', 'Enterprise', 'Starter'];
  const premiumTypes = PREMIUM_SUBSCRIPTION_TYPES;
  const isPremium = premiumTypes.includes(decoded.subscription_type);
  return isPremium;
};

/**
 * Gets the user's subscription type from their JWT token
 * @param {string} token - The JWT token to check (optional, will get from localStorage if not provided)
 * @returns {string|null} - The subscription type or null if not found
 */
export const getSubscriptionType = (token = null) => {
  // const authToken = token || localStorage.getItem(AUTH_TOKEN);
  const authToken = token || localStorage.getItem(SEC_AUTH_M_TK);
  
  if (!authToken) return null;

  const decoded = decodeToken(authToken);
  if (!decoded) return null;

  return decoded.subscription_type || null;
};

/**
 * Gets the user's role from their JWT token
 * @param {string} token - The JWT token to check
 * @returns {string|null} - The user role or null if not found
 */
export const getUserRoleFromToken = (token) => {
  if (!token) return null;

  const decoded = decodeToken(token);
  if (!decoded) return null;

  return decoded.role || null;
};

/**
 * Gets the user's ID (subject) from their JWT token
 * @param {string} token - The JWT token to check
 * @returns {string|null} - The user ID or null if not found
 */
export const getUserIdFromToken = (token) => {
  if (!token) return null;

  const decoded = decodeToken(token);
  if (!decoded) return null;

  return decoded.sub || null;
};

/**
 * Whether JWT marks this account as a first-time user (signup agent onboarding).
 * @param {string|null} token - JWT (optional; falls back to stored auth token)
 * @returns {boolean}
 */
export const isFirstTimeUserFromToken = (token = null) => {
  const authToken = token || localStorage.getItem(SEC_AUTH_M_TK);
  if (!authToken) return false;

  const decoded = decodeToken(authToken);
  if (!decoded) return false;

  return decoded.is_first_time_user === true;
};

/**
 * Gets the username from their JWT token
 * @param {string} token - The JWT token to check
 * @returns {string|null} - The username or null if not found
 */
export const getUsernameFromToken = (token) => {
  if (!token) return null;

  const decoded = decodeToken(token);
  if (!decoded) return null;

  // Use the sub field as username, or check for a specific username field
  return decoded.username || decoded.sub || null;
};

/**
 * Gets the user's email from their JWT token
 * @param {string} token - The JWT token to check
 * @returns {string|null} - The user email or null if not found
 */
export const getUserEmailFromToken = (token) => {
  if (!token) {
    return null;
  }

  const decoded = decodeToken(token);
  if (!decoded) {
    return null;
  }

  // Try multiple possible fields for email
  const email = decoded.email || decoded.sub || decoded.username || decoded.user_email || decoded.user;

  return email || null;
};

/**
 * Gets the plan active status from their JWT token
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if the plan is active, false otherwise
 *
 * Note: In your system:
 * - is_plan_active = false → User is on Free plan
 * - is_plan_active = true → User has active paid subscription (Starter/Professional/Enterprise)
 */
export const isPlanActive = (token) => {
  if (!token) {
    return false;
  }

  const decoded = decodeToken(token);
  if (!decoded) {
    return false;
  }

  // Check if the is_plan_active field exists and is true
  const planActive = decoded.is_plan_active === true;
  return planActive;
};

/**
 * Gets the tenant ID from their JWT token
 * @param {string} token - The JWT token to check
 * @returns {string|number|null} - The tenant ID or null if not found
 */
export const getTenantIdFromToken = (token) => {
  if (!token){
    token = localStorage.getItem('sec_auth_m_tk');
  };

  const decoded = decodeToken(token);
  if (!decoded) return null;

  return decoded.tenant_id || null;
};

/**
 * Comprehensive permission checker that considers role, subscription, and plan status
 * @param {string} token - The JWT token to check
 * @returns {Object} - Object containing all permission-related information
 */
export const getPermissionInfo = (token) => {
  if (!token) {
    return {
      role: null,
      subscriptionType: null,
      isPremium: false,
      isPlanActive: false,
      tenantId: null,
      hasAccess: false
    };
  }

  const decoded = decodeToken(token);
  if (!decoded) {
    return {
      role: null,
      subscriptionType: null,
      isPremium: false,
      isPlanActive: false,
      tenantId: null,
      hasAccess: false
    };
  }

  const role = decoded.role;
  const subscriptionType = decoded.subscription_type;
  const planActive = decoded.is_plan_active === true;
  const tenantId = decoded.tenant_id;

  // Note: hasAccess is now always true for authenticated users - access control based on subscriptionType only
  const hasAccess = true;

  return {
    role,
    subscriptionType,
    isPlanActive: planActive,
    tenantId,
    hasAccess
  };
};



/**
 * Gets the user_id from the JWT token
 * @param {string} token - The JWT token to check
 * @returns {number|null} - The user_id or null if not found
 */
export const getUserIdFromTokenNum = (token) => {
  if (!token) return null;

  const decoded = decodeToken(token);
  if (!decoded) return null;

  return decoded.user_id || null;
};

/**
 * Gets the addon_features array from the JWT token
 * @param {string} token - The JWT token to check
 * @returns {Array} - The addon_features array or empty array if not found
 */
export const getAddonFeaturesFromToken = (token) => {
  if (!token) return [];
  const decoded = decodeToken(token);
  // SonarQube fix - use optional chaining instead of explicit null check
  if (!decoded?.enabled_features) {
    return [];
  }
  // Support both typo and correct spelling for robustness
  const raw =
    decoded.enabled_features.addon_fatures ||
    decoded.enabled_features.addon_features ||
    [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/** Backend add-on key for Fusion+ (CSM conversations). */
export const CSM_CONVERSATIONS_ADDON_KEY = 'CsmConversations';

/**
 * True when JWT addon_features / addon_fatures includes CsmConversations.
 * @param {string} token
 * @returns {boolean}
 */
export const hasCsmConversationsAddon = (token) => {
  const features = getAddonFeaturesFromToken(token);
  return (Array.isArray(features) ? features : []).some(
    (feature) => String(feature || '').toLowerCase() === 'csmconversations'
  );
};

/**
 * Fusion+ access: Pay As You Go subscribers, or monthly plans with the CsmConversations add-on.
 * @param {string} token - JWT token (preferred) or subscription type string
 * @returns {boolean}
 */
export const hasFusionPlusAccess = (token) => {
  if (!token) return false;
  if (isPayAsYouGoSubscription(token)) return true;
  // Subscription-type-only callers cannot check add-ons
  if (typeof token === 'string' && token.split('.').length !== 3) {
    return false;
  }
  return hasCsmConversationsAddon(token);
};

/**
 * @deprecated Use hasFusionPlusAccess instead.
 */
export const hasFusionAddonFeature = (token) => hasFusionPlusAccess(token);

/**
 * True when subscription is Pay As You Go (accepts JWT token or subscription type string).
 * @param {string} [tokenOrSubscriptionType]
 * @returns {boolean}
 */
export const isPayAsYouGoSubscription = (tokenOrSubscriptionType) => {
  let subscriptionType = tokenOrSubscriptionType;
  if (!subscriptionType) return false;
  if (typeof subscriptionType === 'string' && subscriptionType.split('.').length === 3) {
    subscriptionType = getSubscriptionType(subscriptionType);
  }
  if (!subscriptionType) return false;
  if (subscriptionType === 'Pay_As_You_Go') return true;
  const normalized = String(subscriptionType).toLowerCase().replace(/\s+/g, '_');
  return normalized === 'pay_as_you_go';
};

/**
 * Gets the subscription_features array from the JWT token
 * @param {string} token - The JWT token to check
 * @returns {Array} - The subscription_features array or empty array if not found
 */
export const getSubscriptionFeaturesFromToken = (token) => {
  if (!token) return [];
  const decoded = decodeToken(token);
  const raw = decoded?.enabled_features?.subscription_features || [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};


/**
 * Checks if Starter_Voice user can see chat features
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if Starter_Voice user has addons OR WebAiAgent
 */
export const canStarterVoiceSeeChatFeatures = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || decoded.subscription_type !== 'Starter_Voice') {
    return false;
  }

  const addonFeatures = getAddonFeaturesFromToken(token);
  const hasChatAddon = (Array.isArray(addonFeatures) ? addonFeatures : []).some((feature) => {
    const normalized = String(feature || '').toLowerCase();
    return normalized === 'weba iagent'.replace(' ', '') ||
           normalized === 'webbot' ||
           normalized === 'whatsappbot' ||
           normalized === 'instagrambot' ||
           normalized === 'linebot' ||
           normalized === 'facebookbot' ||
           normalized === 'emailbot';
  });

  // Check for WebAiAgent in subscription features
  const subscriptionFeatures = getSubscriptionFeaturesFromToken(token);
  const hasWebAiAgent = (Array.isArray(subscriptionFeatures) ? subscriptionFeatures : [])
    .some((feature) => String(feature || '').toLowerCase() === 'weba iagent'.replace(' ', ''));

  // Return true only when chat-specific entitlement exists
  return hasChatAddon || hasWebAiAgent;
};

